// Phase 6A — SuggestedPartsPanel.
// Display-only chip strip mounted above the parts list inside TicketPartsPanel.
// Clicking a chip opens the existing PartsAssistIntakeModal with a synthesized
// capture (source_vendor='suggestion'). User must confirm — never auto-adds.
//
// Rules enforced visually:
//   - DVI / Job / Pensieve groups are TOP-LEVEL.
//   - Within each group, Repair / Companion / Supplies are SEPARATE sub-sections.
//   - Bucket sub-sections are never blended.
//   - Dismissing a chip is sessionStorage-only; no DB write.
//   - Empty state renders nothing (no wrapper).

import { useMemo, useState, useEffect } from 'react';
import { Stethoscope, Wrench, BarChart3, X, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import type { GroupedSuggestions, PartSuggestion, SuggestionBucket } from '@/lib/parts-suggestions/types';

const BUCKET_LABEL: Record<SuggestionBucket, string> = {
  repair: 'Repair parts',
  companion: 'Companion items',
  supplies: 'Shop supplies',
};

const BUCKET_ORDER: SuggestionBucket[] = ['repair', 'companion', 'supplies'];

interface Props {
  ticketId: string;
  suggestions: GroupedSuggestions;
  onSelect: (s: PartSuggestion) => void;
}

const DISMISS_KEY = (ticketId: string) => `ps:dismissed:${ticketId}`;

function loadDismissed(ticketId: string): Set<string> {
  try {
    const raw = sessionStorage.getItem(DISMISS_KEY(ticketId));
    if (!raw) return new Set();
    const arr = JSON.parse(raw) as unknown;
    return new Set(Array.isArray(arr) ? (arr as string[]) : []);
  } catch {
    return new Set();
  }
}

function saveDismissed(ticketId: string, s: Set<string>) {
  try {
    sessionStorage.setItem(DISMISS_KEY(ticketId), JSON.stringify(Array.from(s)));
  } catch {
    /* ignore quota */
  }
}

export function SuggestedPartsPanel({ ticketId, suggestions, onSelect }: Props) {
  const [dismissed, setDismissed] = useState<Set<string>>(() => loadDismissed(ticketId));

  // Reload dismissed set when ticket changes.
  useEffect(() => {
    setDismissed(loadDismissed(ticketId));
  }, [ticketId]);

  const filtered = useMemo<GroupedSuggestions>(() => ({
    dvi: suggestions.dvi.filter((s) => !dismissed.has(s.key)),
    job: suggestions.job.filter((s) => !dismissed.has(s.key)),
    pensieve: suggestions.pensieve.filter((s) => !dismissed.has(s.key)),
  }), [suggestions, dismissed]);

  const totalShown = filtered.dvi.length + filtered.job.length + filtered.pensieve.length;
  if (totalShown === 0) return null;

  const dismissChip = (key: string) => {
    const next = new Set(dismissed);
    next.add(key);
    setDismissed(next);
    saveDismissed(ticketId, next);
  };

  return (
    <div className="rounded-lg border border-primary/20 bg-primary/5 p-3 space-y-3">
      <div className="flex items-center gap-2">
        <Sparkles className="h-3.5 w-3.5 text-primary" />
        <h4 className="text-xs font-medium uppercase tracking-wide text-foreground">
          Suggested parts
        </h4>
        <Badge variant="outline" className="text-[10px] py-0 h-4 border-primary/30 text-primary">
          {totalShown}
        </Badge>
      </div>

      <SuggestionGroup
        icon={<Stethoscope className="h-3 w-3" />}
        label="Based on inspection"
        items={filtered.dvi}
        onSelect={onSelect}
        onDismiss={dismissChip}
      />
      <SuggestionGroup
        icon={<Wrench className="h-3 w-3" />}
        label="Based on job"
        items={filtered.job}
        onSelect={onSelect}
        onDismiss={dismissChip}
      />
      <SuggestionGroup
        icon={<BarChart3 className="h-3 w-3" />}
        label="Commonly added"
        items={filtered.pensieve}
        onSelect={onSelect}
        onDismiss={dismissChip}
      />

      <p className="text-[10px] text-muted-foreground italic">
        Suggestions are starting points — confirm, edit, or dismiss. Nothing is added automatically.
      </p>
    </div>
  );
}

function SuggestionGroup({
  icon,
  label,
  items,
  onSelect,
  onDismiss,
}: {
  icon: React.ReactNode;
  label: string;
  items: PartSuggestion[];
  onSelect: (s: PartSuggestion) => void;
  onDismiss: (key: string) => void;
}) {
  if (items.length === 0) return null;

  // Group by bucket — buckets render as separate sub-sections, never blended.
  const byBucket: Record<SuggestionBucket, PartSuggestion[]> = {
    repair: [],
    companion: [],
    supplies: [],
  };
  for (const s of items) byBucket[s.bucket].push(s);

  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-1.5 text-[11px] font-medium text-foreground/80">
        {icon}
        <span>{label}</span>
      </div>
      {BUCKET_ORDER.map((b) => {
        const bucketItems = byBucket[b];
        if (bucketItems.length === 0) return null;
        return (
          <div key={b} className="pl-4 space-y-1">
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground/80">
              {BUCKET_LABEL[b]}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {bucketItems.map((s) => (
                <SuggestionChip
                  key={s.key}
                  suggestion={s}
                  onSelect={onSelect}
                  onDismiss={onDismiss}
                />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function SuggestionChip({
  suggestion,
  onSelect,
  onDismiss,
}: {
  suggestion: PartSuggestion;
  onSelect: (s: PartSuggestion) => void;
  onDismiss: (key: string) => void;
}) {
  // Phase 6B — Pensieve chips show explainable meta line under the label:
  //   "Commonly added on this job · 68% · n=34"
  const isPensieve = suggestion.source === 'pensieve';
  const pct =
    suggestion.pensieveFrequencyPct != null
      ? Math.round(suggestion.pensieveFrequencyPct * 100)
      : null;
  const scopeLabel =
    suggestion.pensieveScope === 'job'
      ? 'on this job'
      : suggestion.pensieveScope === 'family'
        ? 'in this category'
        : null;

  return (
    <div
      className="group inline-flex items-center gap-0.5 rounded-full border border-border bg-background hover:border-primary/40 hover:bg-primary/5 transition-colors"
      title={suggestion.reason}
    >
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className={`${isPensieve ? 'h-auto py-1' : 'h-6'} px-2.5 text-[11px] font-normal rounded-full flex flex-col items-start gap-0`}
        onClick={() => onSelect(suggestion)}
      >
        <span>+ {suggestion.label}</span>
        {isPensieve && pct != null && scopeLabel && (
          <span className="text-[9px] leading-tight text-muted-foreground font-normal">
            {scopeLabel} · {pct}% · n={suggestion.pensieveSampleSize}
          </span>
        )}
      </Button>
      <button
        type="button"
        aria-label={`Dismiss ${suggestion.label}`}
        onClick={(e) => {
          e.stopPropagation();
          onDismiss(suggestion.key);
        }}
        className="opacity-0 group-hover:opacity-100 px-1 py-1 text-muted-foreground hover:text-foreground transition-opacity"
      >
        <X className="h-3 w-3" />
      </button>
    </div>
  );
}
